import React, { useState, useCallback, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Share,
  Alert,
} from 'react-native';
import {
  Text,
  Card,
  useTheme,
  Chip,
  IconButton,
  Surface,
  Button,
  Switch,
  Divider,
  Modal,
  Portal,
} from 'react-native-paper';
import type { MasteryState } from '@coffeeplexity/shared';
import { ConfidenceIndicator } from './ConfidenceIndicator';
import { logger } from '../../utils/logger';

export interface ProgressShareData {
  userId: string;
  shareId: string;
  title: string;
  description: string;
  shareType: 'milestone' | 'achievement' | 'journey' | 'skill' | 'custom';
  visibility: 'public' | 'friends' | 'private' | 'anonymous';
  includeData: ShareDataInclusion;
  createdAt: string;
  expiresAt?: string;
  shareUrl?: string;
  analytics: ShareAnalytics;
}

export interface ShareDataInclusion {
  overallProgress: boolean;
  skillBreakdown: boolean;
  timeSpent: boolean;
  achievements: boolean;
  personalInsights: boolean;
  comparisons: boolean;
  predictions: boolean;
  customMessage: boolean;
}

export interface ShareAnalytics {
  views: number;
  interactions: number;
  shares: number;
  comments: number;
  reactions: ShareReaction[];
}

export interface ShareReaction {
  type: 'like' | 'celebrate' | 'inspire' | 'support';
  count: number;
  userReacted: boolean;
}

export interface PrivacySettings {
  defaultVisibility: 'public' | 'friends' | 'private';
  allowAnonymousSharing: boolean;
  includePersonalData: boolean;
  allowComments: boolean;
  allowReactions: boolean;
  autoExpireShares: boolean;
  expireDays: number;
  notifyOnInteraction: boolean;
  shareAnalytics: boolean;
}

export interface ShareTemplate {
  id: string;
  name: string;
  description: string;
  type: 'milestone' | 'achievement' | 'journey' | 'skill' | 'custom';
  defaultInclusion: ShareDataInclusion;
  customizable: boolean;
  preview: string;
}

interface ProgressSharingPanelProps {
  masteryStates: MasteryState[];
  shareHistory: ProgressShareData[];
  privacySettings: PrivacySettings;
  shareTemplates: ShareTemplate[];
  onCreateShare: (shareData: Partial<ProgressShareData>) => void;
  onUpdatePrivacy: (settings: PrivacySettings) => void;
  onDeleteShare: (shareId: string) => void;
  onViewShare: (shareId: string) => void;
  style?: any;
}

type ViewMode = 'create' | 'history' | 'privacy' | 'templates';

export function ProgressSharingPanel({
  masteryStates,
  shareHistory,
  privacySettings,
  shareTemplates,
  onCreateShare,
  onUpdatePrivacy,
  onDeleteShare,
  onViewShare,
  style,
}: ProgressSharingPanelProps) {
  const theme = useTheme();
  const [viewMode, setViewMode] = useState<ViewMode>('create');
  const [selectedTemplate, setSelectedTemplate] =
    useState<ShareTemplate | null>(null);
  const [shareForm, setShareForm] = useState<Partial<ProgressShareData>>({
    title: '',
    description: '',
    shareType: 'milestone',
    visibility: privacySettings.defaultVisibility,
    includeData: {
      overallProgress: true,
      skillBreakdown: false,
      timeSpent: false,
      achievements: true,
      personalInsights: false,
      comparisons: false,
      predictions: false,
      customMessage: true,
    },
  });
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [tempPrivacySettings, setTempPrivacySettings] =
    useState<PrivacySettings>(privacySettings);

  // Calculate shareable stats
  const shareableStats = useMemo(() => {
    const totalSkills = masteryStates.length;
    const masteredSkills = masteryStates.filter(
      m => m.masteryLevel >= 0.8
    ).length;
    const averageMastery =
      masteryStates.reduce((sum, m) => sum + m.masteryLevel, 0) /
      Math.max(totalSkills, 1);
    const improvingSkills = masteryStates.filter(
      m => m.trend === 'improving'
    ).length;

    return {
      totalSkills,
      masteredSkills,
      averageMastery,
      improvingSkills,
      masteryPercentage: Math.round(averageMastery * 100),
    };
  }, [masteryStates]);

  const handleTemplateSelect = useCallback((template: ShareTemplate) => {
    setSelectedTemplate(template);
    setShareForm(prev => ({
      ...prev,
      shareType: template.type,
      includeData: { ...template.defaultInclusion },
      title: template.name,
      description: template.description,
    }));
  }, []);

  const handleShareFormUpdate = useCallback((field: string, value: any) => {
    setShareForm(prev => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleInclusionToggle = useCallback(
    (field: keyof ShareDataInclusion) => {
      setShareForm(prev => ({
        ...prev,
        includeData: {
          ...prev.includeData!,
          [field]: !prev.includeData![field],
        },
      }));
    },
    []
  );

  const handleCreateShare = useCallback(() => {
    if (!shareForm.title || !shareForm.description) {
      Alert.alert(
        'Missing Information',
        'Please provide a title and description for your share.'
      );
      return;
    }

    onCreateShare({
      ...shareForm,
      shareId: `share_${Date.now()}`,
      userId: 'current_user', // This would come from auth context
      createdAt: new Date().toISOString(),
      expiresAt: privacySettings.autoExpireShares
        ? new Date(
            Date.now() + privacySettings.expireDays * 24 * 60 * 60 * 1000
          ).toISOString()
        : undefined,
    });

    // Reset form
    setShareForm({
      title: '',
      description: '',
      shareType: 'milestone',
      visibility: privacySettings.defaultVisibility,
      includeData: {
        overallProgress: true,
        skillBreakdown: false,
        timeSpent: false,
        achievements: true,
        personalInsights: false,
        comparisons: false,
        predictions: false,
        customMessage: true,
      },
    });
    setSelectedTemplate(null);
  }, [shareForm, privacySettings, onCreateShare]);

  const handleNativeShare = useCallback(
    async (shareData: ProgressShareData) => {
      try {
        const shareContent = `Check out my coffee learning progress! 🚀\n\n${shareData.title}\n\n${shareData.description}\n\n${shareData.shareUrl || 'View on CoffeePlexity'}`;

        await Share.share({
          message: shareContent,
          title: shareData.title,
          url: shareData.shareUrl,
        });
      } catch (error) {
        logger.error('Error sharing:', error);
      }
    },
    []
  );

  const handlePrivacyUpdate = useCallback(() => {
    onUpdatePrivacy(tempPrivacySettings);
    setShowPrivacyModal(false);
  }, [tempPrivacySettings, onUpdatePrivacy]);

  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return 'earth';
      case 'friends':
        return 'account-group';
      case 'private':
        return 'lock';
      case 'anonymous':
        return 'incognito';
      default:
        return 'help-circle';
    }
  };

  const getVisibilityColor = (visibility: string) => {
    switch (visibility) {
      case 'public':
        return '#4CAF50';
      case 'friends':
        return '#2196F3';
      case 'private':
        return '#FF9800';
      case 'anonymous':
        return '#9C27B0';
      default:
        return theme.colors.outline;
    }
  };

  const renderCreateShare = () => (
    <ScrollView style={styles.createContainer}>
      {/* Quick Stats Preview */}
      <Card style={styles.statsPreviewCard}>
        <Text variant='titleMedium' style={styles.statsTitle}>
          Your Progress Summary
        </Text>
        <View style={styles.statsGrid}>
          <View style={styles.statItem}>
            <Text variant='headlineSmall' style={styles.statValue}>
              {shareableStats.masteryPercentage}%
            </Text>
            <Text variant='bodySmall' style={styles.statLabel}>
              Average Mastery
            </Text>
          </View>
          <View style={styles.statItem}>
            <Text variant='headlineSmall' style={styles.statValue}>
              {shareableStats.masteredSkills}
            </Text>
            <Text variant='bodySmall' style={styles.statLabel}>
              Skills Mastered
            </Text>
          </View>
          <View style={styles.statItem}>
            <Text variant='headlineSmall' style={styles.statValue}>
              {shareableStats.improvingSkills}
            </Text>
            <Text variant='bodySmall' style={styles.statLabel}>
              Improving
            </Text>
          </View>
        </View>
      </Card>

      {/* Templates */}
      <Card style={styles.templatesCard}>
        <Text variant='titleMedium' style={styles.templatesTitle}>
          Share Templates
        </Text>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.templatesScroll}
        >
          {shareTemplates.map(template => (
            <TouchableOpacity
              key={template.id}
              style={[
                styles.templateCard,
                selectedTemplate?.id === template.id && styles.selectedTemplate,
              ]}
              onPress={() => handleTemplateSelect(template)}
            >
              <Text variant='titleSmall' style={styles.templateName}>
                {template.name}
              </Text>
              <Text variant='bodySmall' style={styles.templateDescription}>
                {template.description}
              </Text>
              <Text variant='bodySmall' style={styles.templatePreview}>
                {template.preview}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </Card>

      {/* Share Form */}
      <Card style={styles.shareFormCard}>
        <Text variant='titleMedium' style={styles.formTitle}>
          Create Share
        </Text>

        {/* Title Input */}
        <View style={styles.inputGroup}>
          <Text variant='labelLarge' style={styles.inputLabel}>
            Title
          </Text>
          <TouchableOpacity
            style={styles.textInput}
            onPress={() => {
              // This would open a text input modal or navigate to input screen
              Alert.prompt(
                'Share Title',
                'Enter a title for your share',
                text => handleShareFormUpdate('title', text),
                'plain-text',
                shareForm.title
              );
            }}
          >
            <Text
              variant='bodyMedium'
              style={[
                styles.inputText,
                !shareForm.title && styles.placeholderText,
              ]}
            >
              {shareForm.title || 'Enter title...'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Description Input */}
        <View style={styles.inputGroup}>
          <Text variant='labelLarge' style={styles.inputLabel}>
            Description
          </Text>
          <TouchableOpacity
            style={[styles.textInput, styles.textArea]}
            onPress={() => {
              Alert.prompt(
                'Share Description',
                'Describe what you want to share',
                text => handleShareFormUpdate('description', text),
                'plain-text',
                shareForm.description
              );
            }}
          >
            <Text
              variant='bodyMedium'
              style={[
                styles.inputText,
                !shareForm.description && styles.placeholderText,
              ]}
            >
              {shareForm.description || 'Describe your progress...'}
            </Text>
          </TouchableOpacity>
        </View>

        {/* Visibility Settings */}
        <View style={styles.inputGroup}>
          <Text variant='labelLarge' style={styles.inputLabel}>
            Visibility
          </Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.visibilityOptions}
          >
            {(['public', 'friends', 'private', 'anonymous'] as const).map(
              visibility => (
                <TouchableOpacity
                  key={visibility}
                  style={[
                    styles.visibilityOption,
                    shareForm.visibility === visibility &&
                      styles.selectedVisibility,
                  ]}
                  onPress={() =>
                    handleShareFormUpdate('visibility', visibility)
                  }
                >
                  <IconButton
                    icon={getVisibilityIcon(visibility)}
                    size={16}
                    iconColor={
                      shareForm.visibility === visibility
                        ? 'white'
                        : getVisibilityColor(visibility)
                    }
                  />
                  <Text
                    variant='labelSmall'
                    style={[
                      styles.visibilityLabel,
                      shareForm.visibility === visibility &&
                        styles.selectedVisibilityLabel,
                    ]}
                  >
                    {visibility.toUpperCase()}
                  </Text>
                </TouchableOpacity>
              )
            )}
          </ScrollView>
        </View>

        {/* Data Inclusion */}
        <View style={styles.inputGroup}>
          <Text variant='labelLarge' style={styles.inputLabel}>
            Include in Share
          </Text>
          <View style={styles.inclusionOptions}>
            {Object.entries(shareForm.includeData || {}).map(([key, value]) => (
              <View key={key} style={styles.inclusionOption}>
                <Text variant='bodyMedium' style={styles.inclusionLabel}>
                  {key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/^./, str => str.toUpperCase())}
                </Text>
                <Switch
                  value={value}
                  onValueChange={() =>
                    handleInclusionToggle(key as keyof ShareDataInclusion)
                  }
                />
              </View>
            ))}
          </View>
        </View>

        {/* Create Button */}
        <Button
          mode='contained'
          onPress={handleCreateShare}
          style={styles.createButton}
          disabled={!shareForm.title || !shareForm.description}
        >
          Create Share
        </Button>
      </Card>
    </ScrollView>
  );

  const renderHistory = () => (
    <ScrollView style={styles.historyContainer}>
      {shareHistory.map(share => (
        <Card key={share.shareId} style={styles.shareHistoryCard}>
          <View style={styles.shareHeader}>
            <View style={styles.shareInfo}>
              <Text variant='titleMedium' style={styles.shareTitle}>
                {share.title}
              </Text>
              <Text variant='bodySmall' style={styles.shareDate}>
                {new Date(share.createdAt).toLocaleDateString()}
              </Text>
            </View>
            <View style={styles.shareVisibility}>
              <IconButton
                icon={getVisibilityIcon(share.visibility)}
                size={16}
                iconColor={getVisibilityColor(share.visibility)}
              />
            </View>
          </View>

          <Text variant='bodyMedium' style={styles.shareDescription}>
            {share.description}
          </Text>

          {/* Analytics */}
          <View style={styles.shareAnalytics}>
            <View style={styles.analyticsItem}>
              <IconButton icon='eye' size={16} />
              <Text variant='bodySmall'>{share.analytics.views}</Text>
            </View>
            <View style={styles.analyticsItem}>
              <IconButton icon='heart' size={16} />
              <Text variant='bodySmall'>{share.analytics.interactions}</Text>
            </View>
            <View style={styles.analyticsItem}>
              <IconButton icon='share' size={16} />
              <Text variant='bodySmall'>{share.analytics.shares}</Text>
            </View>
          </View>

          {/* Actions */}
          <View style={styles.shareActions}>
            <Button
              mode='outlined'
              onPress={() => onViewShare(share.shareId)}
              style={styles.shareActionButton}
            >
              View
            </Button>
            <Button
              mode='outlined'
              onPress={() => handleNativeShare(share)}
              style={styles.shareActionButton}
            >
              Share
            </Button>
            <Button
              mode='outlined'
              onPress={() => onDeleteShare(share.shareId)}
              style={styles.shareActionButton}
              textColor={theme.colors.error}
            >
              Delete
            </Button>
          </View>
        </Card>
      ))}
    </ScrollView>
  );

  const renderPrivacySettings = () => (
    <ScrollView style={styles.privacyContainer}>
      <Card style={styles.privacyCard}>
        <Text variant='titleMedium' style={styles.privacyTitle}>
          Privacy & Sharing Settings
        </Text>

        <View style={styles.privacyOption}>
          <View style={styles.privacyOptionInfo}>
            <Text variant='bodyMedium' style={styles.privacyOptionLabel}>
              Default Visibility
            </Text>
            <Text variant='bodySmall' style={styles.privacyOptionDescription}>
              Default privacy level for new shares
            </Text>
          </View>
          <Button mode='outlined' onPress={() => setShowPrivacyModal(true)}>
            {privacySettings.defaultVisibility.toUpperCase()}
          </Button>
        </View>

        <Divider style={styles.privacyDivider} />

        <View style={styles.privacyOption}>
          <View style={styles.privacyOptionInfo}>
            <Text variant='bodyMedium' style={styles.privacyOptionLabel}>
              Allow Anonymous Sharing
            </Text>
            <Text variant='bodySmall' style={styles.privacyOptionDescription}>
              Share progress without revealing your identity
            </Text>
          </View>
          <Switch
            value={privacySettings.allowAnonymousSharing}
            onValueChange={value =>
              onUpdatePrivacy({
                ...privacySettings,
                allowAnonymousSharing: value,
              })
            }
          />
        </View>

        <View style={styles.privacyOption}>
          <View style={styles.privacyOptionInfo}>
            <Text variant='bodyMedium' style={styles.privacyOptionLabel}>
              Include Personal Data
            </Text>
            <Text variant='bodySmall' style={styles.privacyOptionDescription}>
              Allow sharing of detailed learning analytics
            </Text>
          </View>
          <Switch
            value={privacySettings.includePersonalData}
            onValueChange={value =>
              onUpdatePrivacy({
                ...privacySettings,
                includePersonalData: value,
              })
            }
          />
        </View>

        <View style={styles.privacyOption}>
          <View style={styles.privacyOptionInfo}>
            <Text variant='bodyMedium' style={styles.privacyOptionLabel}>
              Auto-Expire Shares
            </Text>
            <Text variant='bodySmall' style={styles.privacyOptionDescription}>
              Automatically delete shares after {privacySettings.expireDays}{' '}
              days
            </Text>
          </View>
          <Switch
            value={privacySettings.autoExpireShares}
            onValueChange={value =>
              onUpdatePrivacy({
                ...privacySettings,
                autoExpireShares: value,
              })
            }
          />
        </View>

        <View style={styles.privacyOption}>
          <View style={styles.privacyOptionInfo}>
            <Text variant='bodyMedium' style={styles.privacyOptionLabel}>
              Interaction Notifications
            </Text>
            <Text variant='bodySmall' style={styles.privacyOptionDescription}>
              Get notified when others interact with your shares
            </Text>
          </View>
          <Switch
            value={privacySettings.notifyOnInteraction}
            onValueChange={value =>
              onUpdatePrivacy({
                ...privacySettings,
                notifyOnInteraction: value,
              })
            }
          />
        </View>
      </Card>
    </ScrollView>
  );

  const renderContent = () => {
    switch (viewMode) {
      case 'create':
        return renderCreateShare();
      case 'history':
        return renderHistory();
      case 'privacy':
        return renderPrivacySettings();
      default:
        return renderCreateShare();
    }
  };

  return (
    <Surface style={[styles.container, style]} elevation={1}>
      {/* Header */}
      <View style={styles.header}>
        <Text variant='headlineSmall' style={styles.title}>
          Share Progress
        </Text>

        {/* Tab Navigation */}
        <View style={styles.tabNavigation}>
          {[
            { key: 'create', label: 'Create', icon: 'plus' },
            { key: 'history', label: 'History', icon: 'history' },
            { key: 'privacy', label: 'Privacy', icon: 'shield-account' },
          ].map(tab => (
            <TouchableOpacity
              key={tab.key}
              style={[styles.tab, viewMode === tab.key && styles.activeTab]}
              onPress={() => setViewMode(tab.key as ViewMode)}
            >
              <IconButton
                icon={tab.icon}
                size={16}
                iconColor={
                  viewMode === tab.key ? '#6750A4' : theme.colors.onSurface
                }
              />
              <Text
                variant='labelMedium'
                style={[
                  styles.tabLabel,
                  viewMode === tab.key && styles.activeTabLabel,
                ]}
              >
                {tab.label}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Content */}
      {renderContent()}

      {/* Privacy Modal */}
      <Portal>
        <Modal
          visible={showPrivacyModal}
          onDismiss={() => setShowPrivacyModal(false)}
          contentContainerStyle={styles.modalContainer}
        >
          <Card style={styles.modalCard}>
            <Text variant='titleLarge' style={styles.modalTitle}>
              Default Visibility
            </Text>

            {(['public', 'friends', 'private'] as const).map(visibility => (
              <TouchableOpacity
                key={visibility}
                style={[
                  styles.modalOption,
                  tempPrivacySettings.defaultVisibility === visibility &&
                    styles.selectedModalOption,
                ]}
                onPress={() =>
                  setTempPrivacySettings(prev => ({
                    ...prev,
                    defaultVisibility: visibility,
                  }))
                }
              >
                <IconButton
                  icon={getVisibilityIcon(visibility)}
                  size={20}
                  iconColor={getVisibilityColor(visibility)}
                />
                <View style={styles.modalOptionContent}>
                  <Text variant='titleSmall' style={styles.modalOptionLabel}>
                    {visibility.charAt(0).toUpperCase() + visibility.slice(1)}
                  </Text>
                  <Text
                    variant='bodySmall'
                    style={styles.modalOptionDescription}
                  >
                    {visibility === 'public' && 'Visible to everyone'}
                    {visibility === 'friends' && 'Visible to your connections'}
                    {visibility === 'private' && 'Only visible to you'}
                  </Text>
                </View>
              </TouchableOpacity>
            ))}

            <View style={styles.modalActions}>
              <Button
                mode='outlined'
                onPress={() => setShowPrivacyModal(false)}
                style={styles.modalButton}
              >
                Cancel
              </Button>
              <Button
                mode='contained'
                onPress={handlePrivacyUpdate}
                style={styles.modalButton}
              >
                Save
              </Button>
            </View>
          </Card>
        </Modal>
      </Portal>
    </Surface>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    margin: 16,
    borderRadius: 12,
  },
  header: {
    padding: 16,
    paddingBottom: 8,
  },
  title: {
    marginBottom: 16,
    fontWeight: '600',
  },
  tabNavigation: {
    flexDirection: 'row',
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    marginRight: 8,
  },
  activeTab: {
    backgroundColor: 'rgba(103, 80, 164, 0.12)',
  },
  tabLabel: {
    marginLeft: 4,
  },
  activeTabLabel: {
    color: '#6750A4',
    fontWeight: '600',
  },

  // Create Share Styles
  createContainer: {
    flex: 1,
    padding: 16,
  },
  statsPreviewCard: {
    padding: 16,
    marginBottom: 16,
  },
  statsTitle: {
    fontWeight: '600',
    marginBottom: 12,
    textAlign: 'center',
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-around',
  },
  statItem: {
    alignItems: 'center',
  },
  statValue: {
    fontWeight: 'bold',
    color: '#4CAF50',
  },
  statLabel: {
    opacity: 0.7,
    marginTop: 4,
  },
  templatesCard: {
    padding: 16,
    marginBottom: 16,
  },
  templatesTitle: {
    fontWeight: '600',
    marginBottom: 12,
  },
  templatesScroll: {
    flexDirection: 'row',
  },
  templateCard: {
    width: 200,
    padding: 12,
    marginRight: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.1)',
  },
  selectedTemplate: {
    borderColor: '#6750A4',
    backgroundColor: 'rgba(103, 80, 164, 0.05)',
  },
  templateName: {
    fontWeight: '600',
    marginBottom: 4,
  },
  templateDescription: {
    opacity: 0.7,
    marginBottom: 8,
  },
  templatePreview: {
    fontStyle: 'italic',
    opacity: 0.6,
  },
  shareFormCard: {
    padding: 16,
  },
  formTitle: {
    fontWeight: '600',
    marginBottom: 16,
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    marginBottom: 8,
    fontWeight: '600',
  },
  textInput: {
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.2)',
    borderRadius: 8,
    padding: 12,
    minHeight: 48,
  },
  textArea: {
    minHeight: 80,
  },
  inputText: {
    lineHeight: 20,
  },
  placeholderText: {
    opacity: 0.5,
  },
  visibilityOptions: {
    flexDirection: 'row',
  },
  visibilityOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: 'rgba(0,0,0,0.2)',
    marginRight: 8,
  },
  selectedVisibility: {
    backgroundColor: '#6750A4',
    borderColor: '#6750A4',
  },
  visibilityLabel: {
    marginLeft: 4,
  },
  selectedVisibilityLabel: {
    color: 'white',
  },
  inclusionOptions: {
    gap: 8,
  },
  inclusionOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  inclusionLabel: {
    flex: 1,
  },
  createButton: {
    marginTop: 16,
  },

  // History Styles
  historyContainer: {
    flex: 1,
    padding: 16,
  },
  shareHistoryCard: {
    padding: 16,
    marginBottom: 12,
  },
  shareHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  shareInfo: {
    flex: 1,
  },
  shareTitle: {
    fontWeight: '600',
  },
  shareDate: {
    opacity: 0.7,
    marginTop: 2,
  },
  shareVisibility: {
    marginLeft: 8,
  },
  shareDescription: {
    marginBottom: 12,
    lineHeight: 20,
  },
  shareAnalytics: {
    flexDirection: 'row',
    gap: 16,
    marginBottom: 12,
  },
  analyticsItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  shareActions: {
    flexDirection: 'row',
    gap: 8,
  },
  shareActionButton: {
    flex: 1,
  },

  // Privacy Styles
  privacyContainer: {
    flex: 1,
    padding: 16,
  },
  privacyCard: {
    padding: 16,
  },
  privacyTitle: {
    fontWeight: '600',
    marginBottom: 16,
  },
  privacyOption: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 12,
  },
  privacyOptionInfo: {
    flex: 1,
    marginRight: 16,
  },
  privacyOptionLabel: {
    fontWeight: '600',
  },
  privacyOptionDescription: {
    opacity: 0.7,
    marginTop: 2,
  },
  privacyDivider: {
    marginVertical: 8,
  },

  // Modal Styles
  modalContainer: {
    margin: 20,
  },
  modalCard: {
    padding: 24,
  },
  modalTitle: {
    fontWeight: '600',
    marginBottom: 16,
    textAlign: 'center',
  },
  modalOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    marginBottom: 8,
  },
  selectedModalOption: {
    backgroundColor: 'rgba(103, 80, 164, 0.12)',
  },
  modalOptionContent: {
    flex: 1,
    marginLeft: 12,
  },
  modalOptionLabel: {
    fontWeight: '600',
  },
  modalOptionDescription: {
    opacity: 0.7,
    marginTop: 2,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 16,
  },
  modalButton: {
    flex: 1,
  },
});
